import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card, CardHeader, CardContent, Divider, Stack, Typography, Box } from "@mui/material";
import {
  customerCardPanelHeaderSx,
  customerCardPanelLabelSx,
  customerCardPanelTitleTypographyProps,
  customerCardPanelValueSx,
} from "./customerCardPanelStyles";

const LineItem = ({ label, value }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "220px minmax(0, 1fr)",
      columnGap: 1,
      alignItems: "start",
    }}
  >
    <Typography variant="body2" sx={customerCardPanelLabelSx}>
      {label}:
    </Typography>
    <Typography variant="body2" sx={customerCardPanelValueSx}>
      {value || "-"}
    </Typography>
  </Box>
);

const formatCountryList = (items) => {
  if (!items.length) {
    return "-";
  }

  return items.join(", ");
};

const CustomerSettingsSummaryPanel = ({ customerId }) => {
  const [customerDocuments, setCustomerDocuments] = useState([]);
  const [countries, setCountries] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [countryRequirements, setCountryRequirements] = useState([]);

  useEffect(() => {
    if (!customerId) {
      setCustomerDocuments([]);
      setCountries([]);
      setBankAccounts([]);
      setDocumentTypes([]);
      setCountryRequirements([]);
      return;
    }

    const fetchData = async () => {
      const [documentsResponse, countriesResponse, bankAccountsResponse, documentTypesResponse, countryRequirementsResponse] = await Promise.all([
        axios.get(`/api/v1/customerdocuments/entity/${customerId}`),
        axios.get("/api/v1/countries"),
        axios.get(`/api/v1/bankaccountsByEntityId/${customerId}`),
        axios.get("/api/v1/documenttypes"),
        axios.get("/api/v1/countryrequirements"),
      ]);

      setCustomerDocuments(documentsResponse.data || []);
      setCountries(countriesResponse.data || []);
      setBankAccounts(bankAccountsResponse.data || []);
      setDocumentTypes(documentTypesResponse.data || []);
      setCountryRequirements(countryRequirementsResponse.data || []);
    };

    fetchData().catch((error) => console.error("Error loading customer settings summary:", error));
  }, [customerId]);

  const activeDocuments = useMemo(() => customerDocuments.filter((item) => Number(item.deleted) === 0), [customerDocuments]);

  const countryCodeMap = useMemo(() => {
    const result = new Map();
    countries.forEach((country) => {
      result.set(country.countryId, country.countryCode);
    });
    return result;
  }, [countries]);

  const hasDocument = (countryId, documentTypeId) => {
    return activeDocuments.some(
      (doc) => Number(doc.countryId) === Number(countryId) && Number(doc.documentType) === Number(documentTypeId)
    );
  };

  const requirementMap = useMemo(() => {
    const result = new Map();
    countryRequirements.forEach((requirement) => {
      result.set(
        `${Number(requirement.countryId)}-${Number(requirement.documentTypeId)}`,
        requirement.specialRequirements != null ? String(requirement.specialRequirements) : null
      );
    });
    return result;
  }, [countryRequirements]);

  const isCountryIncluded = (countryId, countryListValue) => {
    if (!countryListValue) {
      return true;
    }

    const countryList = Array.isArray(countryListValue)
      ? countryListValue.map((value) => String(value).trim())
      : String(countryListValue)
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean);

    return countryList.includes(String(countryId));
  };

  const hasAllRequirementsForDocument = (countryId, documentTypeId) => {
    if (!hasDocument(countryId, documentTypeId)) {
      return false;
    }

    const requirementType = requirementMap.get(`${Number(countryId)}-${Number(documentTypeId)}`);
    if (!requirementType) {
      return true;
    }

    if (["1", "2", "3"].includes(requirementType) && !hasDocument(countryId, 998)) {
      return false;
    }

    if (["3", "4"].includes(requirementType) && !hasDocument(countryId, 999)) {
      return false;
    }

    return true;
  };

  const getCountryCodesByRequirement = (requirementKey, requirementCountriesKey) => {
    return countries
      .filter((country) => {
        const requiredDocumentTypes = documentTypes.filter((docType) => {
          if (!docType?.[requirementKey]) {
            return false;
          }

          return isCountryIncluded(country.countryId, docType?.[requirementCountriesKey]);
        });

        if (!requiredDocumentTypes.length) {
          return false;
        }

        return requiredDocumentTypes.every((docType) =>
          hasAllRequirementsForDocument(country.countryId, docType.documentTypeId)
        );
      })
      .map((country) => country.countryCode)
      .filter(Boolean);
  };

  const vatCountries = useMemo(() => {
    return getCountryCodesByRequirement("vatRequired", "vatRequiredCountries");
  }, [countries, documentTypes, activeDocuments, requirementMap]);

  const exciseCountries = useMemo(() => {
    return getCountryCodesByRequirement("edRequired", "edRequiredCountries");
  }, [countries, documentTypes, activeDocuments, requirementMap]);

  const primaryBankAccount = useMemo(
    () => bankAccounts.find((item) => item.isDefault) || bankAccounts[0],
    [bankAccounts]
  );

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardHeader
        title="Customer Settings"
        titleTypographyProps={customerCardPanelTitleTypographyProps}
        sx={customerCardPanelHeaderSx}
      />
      <Divider />
      <CardContent
        sx={{
          px: 2,
          pt: 1,
          pb: 1.25,
          flex: 1,
          "&:last-child": { pb: 1.25 },
        }}
      >
        <Stack spacing={0.75}>
          <LineItem label="VAT Countries" value={formatCountryList(vatCountries)} />
          <LineItem label="Excise Countries" value={formatCountryList(exciseCountries)} />
          <LineItem label="SEPA" value="Not configured yet" />
          <LineItem label="Payment Flow Account" value={primaryBankAccount?.iban} />
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CustomerSettingsSummaryPanel;
